import {
  DepositIcon,
  MoreOne,
  TooltipIcon,
  TradeIcon,
  TransferData,
  WithdrawIcon,
} from "assets/Icon";
import { Box, Grid, IconButton, Tooltip, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import ActionTable from "./Component/ActionTable";
import Coin from "./Component/Coin";
import CoinCardWithColumn from "./Component/CoinCardWithColumn";
import CoinCardWithRow from "./Component/CoinCardWithRow";
import { GetMarginWalletList } from "store/api/wallet";
import NumberFormats from "components/NumberFormats";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import { TextCell } from "./Component/TextCell";
import WalletActions from "components/WalletActions";
import http from "config/http";
import useIsMobileView from "hooks/useIsMobileView";
import useNotify from "hooks/useNotify";
import TextEquity from "./Component/TextEquity";
import { VALUE_BALANCE } from "pages/Wallet/constants";
import { ROUTE_PATHS } from "routes/paths";
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

const headers = [
  {
    key: "coin",
    align: "left",
    name: "Coin",
    sortable: true,
    sortKey: "coinName",
  },
  {
    key: "total",
    align: "left",
    name: "Total",
    sortable: true,
    sortKey: "totalBalance",
  },
  {
    key: "available",
    align: "left",
    name: "Available",
    sortable: true,
    sortKey: "availableBalance",
  },

  {
    key: "borrowed",
    align: "left",
    name: "Borrowed",
    sortable: true,
    sortKey: "borrowed",
  },
  {
    key: "interest",
    align: "left",
    name: "Interest",
    sortable: true,
    sortKey: "interest",
  },
  {
    key: "equity",
    align: "left",
    name: "Equity / Equity Value Blnc",
    sortable: true,
    sortKey: "equityValue",
  },
  {
    key: "actions",
    align: "center",
    name: "Actions",
  },
];

export function TableMargin() {
  const notify = useNotify();
  const isMobileView = useIsMobileView();
  const theme = useTheme();

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const transfer = searchParams.get("transfer");
  const borrow = searchParams.get("borrow");
  const repay = searchParams.get("repay");

  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(true);
  const [coinList, setCoinList] = useState();
  const [activeView, setActiveView] = useState("list");
  const [valueBalance, seValueBalance] = useState(0);

  const [showWalletAction, setShowWalletAction] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);

  const handleShowWalletAction = (data) => {
    setSelectedCoin(data);
    setShowWalletAction(true);
  };

  const walletActionItems = [
    {
      icon: (
        <WithdrawIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Borrow",
      onClick: (data) => {
        setSearchParams({
          borrow: data.coinId,
        });
      },
    },
    {
      icon: (
        <DepositIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Repay",
      onClick: (data) => {
        setSearchParams({
          repay: data.coinId,
        });
      },
    },
    {
      icon: (
        <TradeIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Trade",
      onClick: (data) => {
        const USDT = 3;
        const Toman = 4;

        const url =
          data.coinId === USDT || data.coinId === Toman
            ? `${ROUTE_PATHS.trade.basic(USDT, Toman)}?walletTypeId=2`
            : `${ROUTE_PATHS.trade.basic(data.coinId, USDT)}?walletTypeId=2`;

        navigate(url);
      },
    },
    {
      icon: <TransferData color={theme.palette.secondary.main} />,
      text: "Transfer",
      onClick: (data) => {
        setSearchParams({
          transfer: data?.coinId,
          transferSideId: 1,
        });
      },
    },
  ];

  const getMarginWalletList = async (hasLoading = true) => {
    try {
      if (hasLoading) {
        setLoading(true);
      }
      const { data } = await http.get(GetMarginWalletList(limit));
      setCoinList(data?.data);
      setLoading(false);
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
  };

  const coinHeaders = useMemo(() => {
    if (valueBalance === VALUE_BALANCE.USDT) {
      return [
        {
          key: "totalBalance",
          bold: true,
          title: "Total",
        },
        {
          key: "availableBalance",
          bold: true,
          title: "Avbl",
        },
        {
          key: "borrowed",
          bold: true,
          title: "Borrowed",
        },
        {
          key: "interest",
          bold: false,
          title: "Interest",
        },
        {
          key: "equityValue",
          bold: true,
          title: "Equity",
        },

        {
          key: "equityValueUSDT",
          bold: true,
          title: "Equity Blnc (USDT)",
        },
      ];
    } else {
      return [
        {
          key: "totalBalance",
          bold: true,
          title: "Total",
        },
        {
          key: "availableBalance",
          bold: true,
          title: "Avbl",
        },
        {
          key: "borrowed",
          bold: true,
          title: "Borrowed",
        },
        {
          key: "interest",
          bold: false,
          title: "Interest",
        },
        {
          key: "equityValue",
          bold: true,
          title: "Equity",
        },
        {
          key: "equityValueToman",
          bold: true,
          title: "Equity Blnc (Toman)",
        },
      ];
    }
  }, [valueBalance]);

  useEffect(() => {
    getMarginWalletList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, transfer, borrow, repay]);

  const formattedCoinList = coinList?.map((data) => {
    return {
      data: data,
      coin: <Coin coin={data} />,

      total: (
        <>
          {data?.totalBalanceComment ? (
            <Tooltip
              title={
                <TextCell
                  color="secondary"
                  item={data?.totalBalanceComment}
                  component="p"
                />
              }
              placement="top"
              arrow
            >
              <Box display={"flex"} alignItems={"center"} width={"fit-content"} gap={1}>
                <TextCell
                  item={<NumberFormats value={data?.totalBalance} />}
                  component="p"
                />

                <Box mb={"0px"}>
                  <TooltipIcon color={theme.palette.text.main} />
                </Box>
              </Box>
            </Tooltip>
          ) : (
            <TextCell
              item={<NumberFormats value={data?.totalBalance} />}
              component="p"
            />
          )}
        </>
      ),

      available: (
        <>
          {data?.availableBalanceComment ? (
            <Tooltip
              title={
                <TextCell
                  color="secondary"
                  item={data?.availableBalanceComment}
                  component="p"
                />
              }
              placement="top"
              arrow
            >
              <Box display={"flex"} alignItems={"center"} width={"fit-content"} gap={1}>
                <TextCell
                  item={<NumberFormats value={data?.availableBalance} />}
                  component="p"
                />

                <Box mb={"0px"}>
                  <TooltipIcon color={theme.palette.text.main} />
                </Box>
              </Box>
            </Tooltip>
          ) : (
            <TextCell
              item={<NumberFormats value={data?.availableBalance} />}
              component="p"
            />
          )}
        </>
      ),

      borrowed: (
        <>
          {data?.borrowedComment ? (
            <Tooltip
              title={
                <TextCell
                  color="secondary"
                  item={data?.borrowedComment}
                  component="p"
                />
              }
              placement="top"
              arrow
            >
              <Box display={"flex"} alignItems={"center"} width={"fit-content"} gap={1}>
                <TextCell item={data?.borrowed} component="p" />

                <Box mb={"0px"}>
                  <TooltipIcon color={theme.palette.text.main} />
                </Box>
              </Box>
            </Tooltip>
          ) : (
            <TextCell item={data?.borrowed} component="p" />
          )}
        </>
      ),

      interest: (
        <>
          {data?.interestComment ? (
            <Tooltip
              title={
                <TextCell
                  color="secondary"
                  item={data?.interestComment}
                  component="p"
                />
              }
              placement="top"
              arrow
            >
              <Box display={"flex"} alignItems={"center"} width={"fit-content"} gap={1}>
                <TextCell item={data?.interest} component="p" />

                <Box mb={"0px"}>
                  <TooltipIcon color={theme.palette.text.main} />
                </Box>
              </Box>
            </Tooltip>
          ) : (
            <TextCell item={data?.interest} component="p" />
          )}
        </>
      ),

      equity: (
        <>
          {data?.equityValueComment ? (
            <Tooltip
              title={
                <TextCell
                  color="secondary"
                  item={data?.equityValueComment}
                  component="p"
                />
              }
              placement="top"
              arrow
            >
              <span>
                <TextEquity item={data} valueBalance={valueBalance} />
              </span>
            </Tooltip>
          ) : (
            <TextEquity item={data} valueBalance={valueBalance} />
          )}
        </>
      ),

      actions: (
        <IconButton color="text" onClick={() => handleShowWalletAction(data)}>
          <MoreOne color={theme.palette.text.secondary} />
        </IconButton>
      ),
    };
  });

  return (
    <>
      <ActionTable
        setLimit={setLimit}
        limit={limit}
        setActiveView={setActiveView}
        activeView={activeView}
        valueBalance={valueBalance}
        seValueBalance={seValueBalance}
      />

      {isMobileView ? (
        <>
          {" "}
          {coinList?.map((coin) => (
            <CoinCardWithRow
              coin={coin}
              headers={coinHeaders}
              onClick={(coin) => {
                navigate(`/wallet/margin/${coin?.coinId}`);
              }}
            />
          ))}
        </>
      ) : (
        <>
          {activeView === "list" ? (
            <>
              {!loading ? (
                <Table ble headers={headers} items={formattedCoinList} />
              ) : (
                <TableSkeleton />
              )}
            </>
          ) : (
            <Grid container spacing={2}>
              {coinList?.map((coin) => (
                <Grid item lg={3} xs={6} sm={6} md={4} key={coin?.id}>
                  <CoinCardWithColumn
                    coin={coin}
                    headers={coinHeaders}
                    onAction={(data) => handleShowWalletAction(data)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      <WalletActions
        hideBookmark
        items={walletActionItems}
        open={showWalletAction}
        coin={selectedCoin}
        onClose={() => {
          setShowWalletAction(false);
        }}
      />
    </>
  );
}

export default TableMargin;
